import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { HrBell } from "../components/hr/HrBell";
import { TeamChat } from "../components/hr/TeamChat";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { prefetch } from "../lib/apiCache";
import { PdfViewerHost, useUnstickBody } from "../components/billing/PdfViewer";
import GlobalSearch from "./GlobalSearch";
import {
  LayoutDashboard,
  ClipboardList,
  Truck,
  Wallet,
  LogOut,
  Armchair,
  Menu,
  X,
  CalendarDays,
  Users,
  Contact,
  Wrench,
  NotebookPen,
  HardHat,
  Shirt,
  Hammer,
  Banknote,
  Package,
  Receipt,
  BarChart3,
  ShoppingBag,
  Image,
  Sparkles,
  Bell,
  Send,
  MessageCircle as MsgIcon,
  CheckCircle2,
  Loader2,
  ArrowLeft,
} from "lucide-react";

const FEEDBACK_LAST_SEEN_KEY = "adams.feedback.lastSeen";

// Round 64 — sidebar new-feedback badge. We poll the recent-feedback
// list every 90s (cheap query, capped at 25 rows) and tally how many
// have a `feedback_submitted_at` newer than the user's last visit to
// /customers. Visiting /customers stamps `now` into localStorage so
// the badge clears. Owner/admin-only — staff don't see /finance etc
// already, but /customers is shared, so we still show the badge.
function useFeedbackBadge(isOnCustomers, off) {
  const [count, setCount] = useState(0);

  // Read last-seen timestamp; default to first-mount-time so we don't
  // splash an alarmingly large number the very first session.
  const lastSeen = () => {
    try {
      return localStorage.getItem(FEEDBACK_LAST_SEEN_KEY) || "";
    } catch {
      return "";
    }
  };

  // Mark all current feedback as "seen" once the user lands on /customers.
  useEffect(() => {
    if (!isOnCustomers) return;
    try {
      localStorage.setItem(FEEDBACK_LAST_SEEN_KEY, new Date().toISOString());
    } catch { /* noop */ }
    setCount(0);
  }, [isOnCustomers]);

  // Bootstrap on first mount: stamp "now" if nothing was ever seen,
  // then poll for newer entries.
  // Round 123.2 — Pause polling while the tab is hidden.
  useEffect(() => {
    if (off) return undefined;
    if (!lastSeen()) {
      try { localStorage.setItem(FEEDBACK_LAST_SEEN_KEY, new Date().toISOString()); }
      catch { /* noop */ }
    }
    let dead = false;
    let timerId = null;
    const tick = async () => {
      try {
        const { data } = await api.get("/customers/feedback?limit=25");
        if (dead) return;
        const since = lastSeen();
        const fresh = (data || []).filter(
          (r) => (r.feedback_submitted_at || "") > since
        );
        setCount(fresh.length);
      } catch { /* silent — RBAC may 403 for staff once /customers is admin-only */ }
    };
    const start = () => {
      if (timerId != null) return;
      timerId = setInterval(tick, 90_000);
    };
    const stop = () => {
      if (timerId != null) { clearInterval(timerId); timerId = null; }
    };
    tick();
    if (!document.hidden) start();
    const onVis = () => {
      if (document.hidden) stop();
      else { tick(); start(); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      dead = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return count;
}

// Round 68 — unread customer messages badge.
// Round 123.2 — Perf: bumped 10 s → 30 s cadence and paused when the
// tab is hidden. The old 10 s tick fired /admin/messages/unread-count
// on every open tab endlessly, keeping the ops desktop busy for a
// counter that changes at most a few times per hour.
function useMessagesBadge(off) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (off) return undefined;
    let dead = false;
    let timerId = null;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/messages/unread-count");
        if (!dead) setCount(Number(data?.unread || 0));
      } catch { /* 403 for guests */ }
    };
    const start = () => {
      if (timerId != null) return;
      timerId = setInterval(tick, 30_000);
    };
    const stop = () => {
      if (timerId != null) { clearInterval(timerId); timerId = null; }
    };
    tick();
    if (!document.hidden) start();
    const onVis = () => {
      if (document.hidden) stop();
      else { tick(); start(); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      dead = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return count;
}

const ROOT_ADMIN = "9243425500";

// Round 95 — Role-based navigation.
// Roles: admin | manager | supervisor | staff (default legacy staff = manager-lite)
// Admin: full access.
// Manager: Dashboard, Orders, Logistics, Calendar, Customers, Photos, Vendors, own Team profile.
// Supervisor: Dashboard, Orders, Logistics, Calendar, Labour, Laundry, own Team profile.
export const NAV_ALL = {
  dashboard:    { to: "/",              label: "Dashboard",     icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  orders:       { to: "/orders",        label: "Orders",        icon: ClipboardList,   tid: "nav-orders" },
  logistics:    { to: "/logistics",     label: "Logistics",     icon: Truck,           tid: "nav-logistics" },
  calendar:     { to: "/calendar",      label: "Calendar",      icon: CalendarDays,    tid: "nav-calendar" },
  customers:    { to: "/customers",     label: "Customers",     icon: Contact,         tid: "nav-customers" },
  photos:       { to: "/photos",        label: "Photos",        icon: Image,           tid: "nav-photos" },
  vendors:      { to: "/vendors",       label: "Vendors",       icon: Wrench,          tid: "nav-vendors" },
  finance:      { to: "/finance",       label: "Finance",       icon: Wallet,          tid: "nav-finance" },
  procurement:  { to: "/procurement",   label: "Procurement",   icon: ShoppingBag,     tid: "nav-procurement" },
  billing:      { to: "/billing",       label: "Billing",       icon: Receipt,         tid: "nav-billing" },
  labour:       { to: "/labour",        label: "Labour",        icon: HardHat,         tid: "nav-labour" },
  laundry:      { to: "/laundry",       label: "Laundry",       icon: Shirt,           tid: "nav-laundry" },
  repairs:      { to: "/repairs",       label: "Furniture Repair", icon: Hammer,       tid: "nav-repairs" },
  cashPayments: { to: "/cash-payments", label: "Drivers & Staff Payments", icon: Banknote,        tid: "nav-cash-payments" },
  inventory:    { to: "/inventory",     label: "Inventory Management", icon: Package,         tid: "nav-inventory" },
  workbook:     { to: "/workbook",      label: "Workbook",      icon: NotebookPen,     tid: "nav-workbook" },
  users:        { to: "/users",         label: "Team & HR",     icon: Users,           tid: "nav-users" },
};

const ROLE_NAV = {
  admin: [
    "dashboard","orders","logistics","calendar","customers","photos","vendors",
    "finance","procurement","billing","labour","laundry","repairs","cashPayments","inventory","workbook","users",
  ],
  manager: [
    "dashboard","orders","logistics","calendar","customers","photos","vendors",
    "procurement","billing","users",
  ],
  // Round 126.30 — Accountant: manager-level nav PLUS full billing access.
  accountant: [
    "dashboard","orders","logistics","calendar","customers","photos","vendors",
    "procurement","billing","finance","users",
  ],
  supervisor: [
    "dashboard","orders","logistics","calendar","labour","laundry","repairs","cashPayments","inventory","users",
  ],
  hr: ["users"],
  employee: ["users"],
};

export default function AppShell() {
  useUnstickBody();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isRootUser = user?.mobile === ROOT_ADMIN;
  const teamOnly = !isRootUser && (user?.pages ? user.pages.every((p) => p === "users") : ["hr", "employee"].includes(user?.role));
  const hrOnly = teamOnly && !loc.pathname.startsWith("/users");
  const [open, setOpen] = useState(false);
  // Round 64 — new-feedback badge on the Customers tab.
  const limited = teamOnly;
  const newFeedbackCount = useFeedbackBadge(loc.pathname.startsWith("/customers"), limited);
  const unreadMessages = useMessagesBadge(limited);

  const nav = React.useMemo(() => {
    let role = user?.role || "manager";
    if (user?.mobile === ROOT_ADMIN) role = "admin";
    if (role === "staff") role = "manager"; // legacy
    if (role !== "admin" && Array.isArray(user?.pages)) return user.pages.map((k) => NAV_ALL[k]).filter(Boolean);
    const base = ROLE_NAV[role] || ROLE_NAV.manager;
    const keys = user?.billing_access && role !== "supervisor" && !base.includes("billing") ? [...base.slice(0, -1), "billing", base[base.length - 1]] : base;
    return keys.map((k) => NAV_ALL[k]).filter(Boolean);
  }, [user]);

  const current = nav.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)));
  const offRole = !isRootUser && Array.isArray(user?.pages) && !current && Object.values(NAV_ALL).some((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)));

  React.useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  // Round 124.11 — Warm the three hot endpoints once at first mount so
  // Orders / Logistics / Dashboard all feel instant on the first tap.
  // No await — pure background prefetch. TTLs are already tuned in
  // apiCache.js so this only fires per-tab-session and hydrates from
  // sessionStorage on reload.
  React.useEffect(() => {
    if (!user || limited) return;
    prefetch("/orders", null, { swr: true });
    prefetch("/customers", null, { swr: true, ttlMs: 120_000 });
    prefetch("/dashboard/summary", null, { swr: true, ttlMs: 30_000 });
  }, [user]);

  return hrOnly ? <Navigate to="/users" replace /> : offRole && nav[0] ? <Navigate to={nav[0].to} replace /> : (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-shell">
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-white border-r border-slate-200 flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-5 flex items-center gap-3 border-b border-slate-200">
          <div className="h-10 w-10 rounded-md bg-white border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
            <img
              src="/adams-logo.jpeg"
              alt="Adams"
              className="h-7 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold tracking-tight text-slate-950 leading-tight">
              Adams
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Operations Desktop
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="lg:hidden h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {nav.map((n) => {
            const Icon = n.icon;
            const showBadge = n.to === "/customers" && newFeedbackCount > 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-red-50 text-red-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{n.label}</span>
                {showBadge && (
                  <span
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-extrabold tabular-nums shadow-sm"
                    data-testid="nav-feedback-badge"
                    aria-label={`${newFeedbackCount} new feedback`}
                    title={`${newFeedbackCount} new feedback since your last visit`}
                  >
                    {newFeedbackCount > 99 ? "99+" : newFeedbackCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold shrink-0">
              {(user?.name || "A").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">
                {user?.name || "Admin"}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {user?.mobile}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              title="Sign out"
              className="h-8 w-8 shrink-0 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setOpen(true)}
              data-testid="menu-toggle-btn"
              className="lg:hidden h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-700"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold truncate">
                Adams · Operations Desktop
              </div>
              <div className="font-heading font-bold text-slate-900 truncate">
                {current?.label || "Dashboard"}
              </div>
            </div>
          </div>

          <div className="flex-1 max-w-md">
            <GlobalSearch />
          </div>

          <HrBell />
          <TeamChat />
          {!limited && <MessagesBell count={unreadMessages} />}

          <div className="text-xs sm:text-sm text-slate-500 shrink-0 text-right hidden md:block">
            <div>
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto pb-24 lg:pb-8">
          <Outlet />
          <PdfViewerHost />
        </main>
      </div>
      {/* Round 123.15 — Mobile bottom nav. Fixed to viewport bottom on
          screens < lg. Provides one-tap access to the 5 most-used
          pages so ops on their phone doesn't have to open the
          hamburger sidebar. Hidden on desktop. */}
      <MobileBottomNav nav={nav} current={current} />
    </div>
  );
}


// Round 123.15 — Bottom nav for mobile / tablet.
function MobileBottomNav({ nav, current }) {
  // Priority order for the bottom slots. We pick the FIRST 5 nav items
  // that match this order and are present in the user's role nav.
  // Round 123.16 — Labour promoted above Customers per user request
  // (checked-in count / attendance is a daily-first task).
  const PRIORITY = [
    "nav-dashboard", "nav-orders", "nav-logistics",
    "nav-calendar", "nav-labour", "nav-customers",
    "nav-finance", "nav-inventory",
  ];
  const byTid = Object.fromEntries((nav || []).map((n) => [n.tid, n]));
  const picks = [];
  for (const tid of PRIORITY) {
    if (byTid[tid]) picks.push(byTid[tid]);
    if (picks.length === 5) break;
  }
  if (picks.length === 0) return null;
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] pb-[env(safe-area-inset-bottom)]"
      data-testid="mobile-bottom-nav"
      aria-label="Primary"
    >
      <ul className="grid grid-cols-5">
        {picks.map((n) => {
          const Icon = n.icon;
          const active = current?.tid === n.tid;
          return (
            <li key={n.tid}>
              <NavLink
                to={n.to}
                end={n.end}
                data-testid={`bottom-${n.tid}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-[10px] font-heading font-bold uppercase tracking-wider transition-colors ${
                    (active || isActive)
                      ? "text-red-700 bg-red-50"
                      : "text-slate-500 hover:text-slate-800"
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                <span className="truncate max-w-[64px]">{n.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}


// Round 68 — Admin Customer Messages inbox (compact dialog + bell badge).
import { toast } from "sonner";

function MessagesBell({ count }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  // Auto-close dialog on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative h-9 px-2.5 sm:px-3 rounded-full inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white shadow-sm shrink-0 transition-colors"
        title="Customer messages"
        data-testid="messages-bell-btn"
      >
        <Bell className="h-4 w-4" />
        <span className="hidden sm:inline text-xs font-bold">Customers</span>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-amber-400 text-slate-900 ring-2 ring-white text-[10px] font-bold flex items-center justify-center" data-testid="messages-bell-badge">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && <MessagesDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function MessagesDialog({ onClose }) {
  const [inbox, setInbox] = useState([]); // raw messages (customer-authored) grouped later
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const threadRef = React.useRef(null);

  const loadInbox = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/messages");
      setInbox(data || []);
    } finally { if (!silent) setLoading(false); }
  };

  const loadThread = async (customerId, silent = false) => {
    if (!customerId) return;
    try {
      const [t, s] = await Promise.all([
        api.get(`/admin/messages/thread/${customerId}`),
        api.get(`/admin/chat/sessions?customer_id=${customerId}`),
      ]);
      setThread(t.data || []);
      setSessions(s.data || []);
    } catch (err) {
      // silent — surfaced via inbox reload
      void err;
    }
    if (!silent) loadInbox(true);
  };

  useEffect(() => {
    loadInbox();
    const iv = setInterval(() => {
      loadInbox(true);
      if (selectedId) loadThread(selectedId, true);
    }, 10000);
    return () => clearInterval(iv);
  }, [selectedId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Auto-scroll thread to bottom on new msgs
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [thread.length]);

  // When selection changes, load full thread
  useEffect(() => {
    if (selectedId) loadThread(selectedId);
    else { setThread([]); setSessions([]); }
  }, [selectedId]);

  // Group inbox messages by customer_id, latest first
  const customers = React.useMemo(() => {
    const byId = new Map();
    for (const m of inbox) {
      const cid = m.customer_id;
      if (!cid) continue;
      const cur = byId.get(cid) || {
        customer_id: cid,
        customer_name: m.customer_name || m.customer_phone || "Customer",
        customer_phone: m.customer_phone || m.customer_mobile || "",
        last_msg: m,
        unread: 0,
        latest_at: m.created_at,
        last_category: m.session_category || "",
      };
      cur.unread += m.unread_by_admin ? 1 : 0;
      if ((m.created_at || "") > (cur.latest_at || "")) {
        cur.latest_at = m.created_at;
        cur.last_msg = m;
        cur.last_category = m.session_category || cur.last_category;
      }
      byId.set(cid, cur);
    }
    return Array.from(byId.values()).sort((a, b) => (b.latest_at || "").localeCompare(a.latest_at || ""));
  }, [inbox]);

  const openSession = sessions.find((s) => s.status === "open");
  const selectedCustomer = customers.find((c) => c.customer_id === selectedId);

  const send = async () => {
    const text = reply.trim();
    if (!text || !selectedId) return;
    setSending(true);
    try {
      await api.post(`/admin/messages/thread/${selectedId}/send`, { body: text });
      setReply("");
      await loadThread(selectedId, true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const resolveCurrent = async () => {
    if (!openSession) return;
    if (!window.confirm("Mark this chat resolved? Customer will see a closed banner + option to start a new chat.")) return;
    try {
      await api.post(`/admin/chat/sessions/${openSession.id}/resolve`);
      toast.success("Chat resolved");
      await loadThread(selectedId, true);
      await loadInbox(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-slate-950/60 flex sm:items-start sm:justify-center sm:p-4 sm:pt-10"
      onClick={onClose}
      data-testid="admin-messages-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-5xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden h-[100dvh] sm:h-[85vh]"
      >
        <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200 flex items-center gap-2">
          {/* Mobile back arrow when a customer is open */}
          {selectedId && (
            <button
              onClick={() => setSelectedId(null)}
              className="sm:hidden h-8 w-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center shrink-0"
              data-testid="admin-messages-back-btn"
              aria-label="Back to conversations"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <MsgIcon className={`h-5 w-5 text-red-700 shrink-0 ${selectedId ? "hidden sm:inline-block" : ""}`} />
          <h3 className="font-heading font-extrabold text-slate-950 text-sm sm:text-base truncate">
            {selectedId ? (
              <>
                <span className="sm:hidden">{selectedCustomer?.customer_name || "Chat"}</span>
                <span className="hidden sm:inline">Customer Messages</span>
              </>
            ) : (
              "Customer Messages"
            )}
          </h3>
          <span className="text-[11px] sm:text-xs text-slate-500 truncate hidden sm:inline">
            {customers.length} conversation{customers.length !== 1 ? "s" : ""} · {customers.reduce((s, c) => s + c.unread, 0)} unread
          </span>
          <button onClick={onClose} className="ml-auto h-8 w-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center shrink-0" data-testid="admin-messages-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* LEFT: customer list — full width on mobile, w-72 on desktop, hidden on mobile when a chat is open */}
          <aside
            className={`${selectedId ? "hidden sm:flex" : "flex"} flex-col w-full sm:w-72 sm:border-r border-slate-200 overflow-y-auto shrink-0 bg-slate-50`}
            data-testid="admin-msg-customer-list"
          >
            {loading ? (
              <div className="text-center py-8 text-sm text-slate-500">Loading…</div>
            ) : customers.length === 0 ? (
              <div className="text-center py-10 px-4 text-sm text-slate-500">
                No customer conversations yet.
              </div>
            ) : customers.map((c) => {
              const active = c.customer_id === selectedId;
              return (
                <button
                  key={c.customer_id}
                  onClick={() => setSelectedId(c.customer_id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-100 flex flex-col gap-0.5 transition ${active ? "bg-white" : "hover:bg-white"}`}
                  data-testid={`admin-msg-customer-${c.customer_id}`}
                >
                  <div className="flex items-center gap-1.5">
                    <div className="font-bold text-sm text-slate-950 truncate flex-1">{c.customer_name}</div>
                    {c.unread > 0 && (
                      <span className="h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`admin-msg-unread-${c.customer_id}`}>{c.unread}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {c.last_category && <span className="text-[9px] font-bold bg-amber-100 text-amber-800 px-1 rounded uppercase tracking-wider">{c.last_category}</span>}
                    <span className="text-[10px] text-slate-500 font-mono">{c.customer_phone}</span>
                  </div>
                  <div className="text-xs text-slate-600 truncate mt-0.5">{c.last_msg?.body || "—"}</div>
                  <div className="text-[10px] text-slate-400">{c.latest_at ? c.latest_at.slice(0, 10) : ""}</div>
                </button>
              );
            })}
          </aside>

          {/* RIGHT: chat thread — hidden on mobile when no customer selected */}
          <section
            className={`${selectedId ? "flex" : "hidden sm:flex"} flex-1 flex-col min-w-0`}
            data-testid="admin-msg-thread-pane"
          >
            {!selectedCustomer ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2">
                <MsgIcon className="h-10 w-10" />
                <div className="text-sm">Pick a customer from the left to view the conversation</div>
              </div>
            ) : (
              <>
                <header className="px-3 sm:px-4 py-2 sm:py-2.5 border-b border-slate-200 flex items-center gap-2 bg-white">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-extrabold text-slate-950 text-sm truncate">{selectedCustomer.customer_name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono">{selectedCustomer.customer_phone}</span>
                      {openSession && <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 px-1 rounded uppercase whitespace-nowrap">Open · {openSession.category}</span>}
                      {!openSession && <span className="text-[9px] font-bold bg-slate-200 text-slate-700 px-1 rounded uppercase whitespace-nowrap">No open chat</span>}
                    </div>
                  </div>
                  {openSession && (
                    <button
                      onClick={resolveCurrent}
                      className="h-8 px-2 sm:px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] sm:text-xs font-semibold inline-flex items-center gap-1 sm:gap-1.5 shrink-0"
                      data-testid="admin-msg-resolve-btn"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /><span className="hidden sm:inline">Resolve chat</span><span className="sm:hidden">Resolve</span>
                    </button>
                  )}
                </header>

                <div ref={threadRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 space-y-2 bg-slate-50">
                  {thread.length === 0 ? (
                    <div className="text-center py-10 text-sm text-slate-400">No messages yet.</div>
                  ) : thread.map((m, i) => (
                    <React.Fragment key={m.id}>
                      {i > 0 && thread[i - 1].session_id && m.session_id && thread[i - 1].session_id !== m.session_id && (
                        <div className="flex items-center gap-2 my-2">
                          <div className="flex-1 border-t border-dashed border-slate-300" />
                          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 px-2">
                            New chat · {m.session_category || "General"}
                          </div>
                          <div className="flex-1 border-t border-dashed border-slate-300" />
                        </div>
                      )}
                      <AdminBubble m={m} />
                    </React.Fragment>
                  ))}
                </div>

                <div className="border-t border-slate-200 px-2 sm:px-3 py-2 flex items-end gap-2 bg-white shrink-0">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    rows={1}
                    placeholder={openSession ? "Type your reply…" : "No open chat. Sending will start a new session."}
                    className="flex-1 min-w-0 px-3 py-2 rounded-md border border-slate-300 text-base sm:text-sm resize-none focus:outline-none focus:border-red-400 min-h-[40px] max-h-[100px]"
                    style={{ fontSize: "16px" }}
                    data-testid="admin-msg-reply-input"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !reply.trim()}
                    className="h-10 w-10 sm:w-auto sm:px-4 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60 shrink-0"
                    data-testid="admin-msg-send-btn"
                    aria-label="Send reply"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    <span className="hidden sm:inline">Send</span>
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AdminBubble({ m }) {
  const mine = !!m.is_from_admin; // admin's own reply on the right
  const when = m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${mine ? "bg-red-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
        {!mine && <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">{m.sender_name || m.customer_name || "Customer"}</div>}
        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
        <div className={`text-[10px] mt-0.5 text-right ${mine ? "text-red-100/80" : "text-slate-400"}`}>{when}</div>
      </div>
    </div>
  );
}
